import DashShell from "@components/common/DashShell";
import { Crumbs, SpinnerLoader } from "@ucchub/ui";
import { useTranslation } from "react-i18next";
import useCustomerDetails from "@hooks/customer/useCustomerDetails";
import CustomerBanner from "@components/customer/CustomerBanner";
import breadCrumbs from "@json/common/crumbs.json";
import CustomerError from "@components/customer/CustomerError";
import { useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Container from "@vfuk/core-container";
import Heading from "@vfuk/core-heading";
import { Tab, Tabs } from "@vfuk/core-tabs";
import CommercialCriteriaTab from "./CommercialCriteriaTab";
import BillingContextTab from "./BillingContextTab";
import useProductList from "@hooks/product/useProductList";
import AccountConfigurationTab from "./AccountConfigurationTab";

type TabType = "accountConfig" | "commercialCriteria" | "billingContext";

type ProductConfigurationProps = {
  mode: "add" | "view";
};

const ProductConfiguration = ({ mode }: ProductConfigurationProps) => {
  const { t } = useTranslation();
  const { customerDetails, isCustomerLoading, fetchCustomerDetails, error } =
    useCustomerDetails();

  const [productName, setProductName] = useState("");

  const { customerId, productId } = useParams();

  const { productList, fetchProductList } = useProductList();

  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = (searchParams?.get("tab") as TabType) || "accountConfig";

  useEffect(() => {
    if (customerId) {
      fetchCustomerDetails(customerId);
    }
  }, [customerId, mode]);

  useEffect(() => {
    if (customerId) {
      fetchProductList();
    }
  }, [customerId]);

  useEffect(() => {
    if (productList.length) {
      // find selected product
      const product = productList.find(({ id }) => id === productId);

      if (product) {
        setProductName(product?.name);
      }
    }
  }, [productList]);

  const headText = isCustomerLoading
    ? t("core:customers:loading")
    : customerDetails?.name;

  return (
    <DashShell>
      <Crumbs items={breadCrumbs.product_config} />
      <CustomerBanner
        pid={customerDetails?.pid}
        headText={headText || ""}
        status={customerDetails?.status}
      />
      {error && <CustomerError error={error} />}
      {isCustomerLoading && (
        <SpinnerLoader
          srText={t("core:accountConfig:loading") || "Loading ..."}
          text={t("core:accountConfig:loading") || "Loading ..."}
        />
      )}

      {customerDetails && !isCustomerLoading && (
        <Container appearance="primary" elevation>
          <Heading
            level={4}
            weight={3}
            noMargin
            text={`Product - ${productName}`}
          />
          <Tabs
            externallySetTabId={activeTab}
            onTabClick={(tabId) => {
              searchParams.set("tab", tabId);
              setSearchParams(searchParams);
            }}
            tabs={{ width: "stretch" }}
          >
            <Tab
              text={t("core:accountConfig:account_config") || undefined}
              id="accountConfig"
            >
              <AccountConfigurationTab
                mode={mode}
                customerName={customerDetails.name}
                customerId={customerDetails.id}
                productName={productName}
                productId={productId}
              />
            </Tab>
            <Tab
              text={t("core:accountConfig:commercial_criteria") || undefined}
              id="commercialCriteria"
            >
              <CommercialCriteriaTab customerId={customerDetails.id} />
            </Tab>

            <Tab
              text={t("core:accountConfig:billing_context") || undefined}
              id="billingContext"
            >
              <BillingContextTab customerId={customerDetails.id} />
            </Tab>
          </Tabs>
        </Container>
      )}
    </DashShell>
  );
};

export default ProductConfiguration;
